import { useEffect, useState } from "react";

import {
  Button,
  TextField,
  Typography,
  TextArea,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { useInput, useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useSendErrorReportMutation } from "../../api/mailApi";

import { useCurrentUser } from "../../hooks/authorization/useCurrentUser";
import { ErrorUserData } from "../../models/ErrorMessage/ErrorUserData";
import { ErrorUserDataScheme } from "../../models/ErrorMessage/ErrorUserDataScheme";
import styles from "./CreateErrorReport.module.scss";

export const CreateErrorReport = () => {
  const [errorData, setErrorData] = useState<ErrorUserData>(
    {} as ErrorUserData
  );
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const [isMessageModalOpened, setIsMessageModalOpened] = useState(false);
  const [isCancelModalOpened, setIsCancelModalOpened] = useState(false);
  const [
    sendErrorReport,
    { isLoading: isSendingErrorReport },
  ] = useSendErrorReportMutation();
  const user = useCurrentUser();
  const handleSend = async () => {
    handleToggleMessageModal();
  };

  const [isMailSuccessfullySent, setIsMailSuccessfullySent] = useState(false);

  const handleToggleMessageModal = () => {
    const isValid = doValidations();
    if (isValid) {
      setIsMessageModalOpened((prev) => !prev);
    }
  };

  const handleToggleCancelModal = () => {
    setIsCancelModalOpened(
      (openConfirmSuccessModal) => !openConfirmSuccessModal
    );
  };

  const handleCloseIsMailSuccessfullySentModal = () => {
    setIsMailSuccessfullySent(false);
  };

  const handleConfirmCancelModal = () => {
    window.close();
  };

  const handleConfirmMessageModal = () => {
    handleSave();
  };

  const handleSave = async () => {
    if (isSendingErrorReport) {
      return;
    }

    try {
      await sendErrorReport(errorData).unwrap();
      setIsMessageModalOpened(false);
      setIsMailSuccessfullySent(true);
    } catch (error) {
      setIsMailSuccessfullySent(false);
    }
  };

  useEffect(() => {
    (async () => {
      let search = window.location.search;
      let params = new URLSearchParams(search);

      const errorDate = new Date().toLocaleString("en-GB", {
        timeZone: "Europe/Zagreb",
      });

      setErrorData({
        ...errorData,
        id:
          params?.get("id") !== "undefined" || params?.get("id") !== null
            ? params?.get("id")
            : null,
        message: params.get("message"),
        date: errorDate,
        url: params.get("url"),
        browser: checkBrowser(),
        userOib: user.oib,
        currentPedagogicalYearInstitutionId: user.institutionId,
      });
    })();
  }, []);

  const [errors, doValidations] = useValidation({
    data: errorData,
    validationSchema: ErrorUserDataScheme,
  });

  const checkBrowser = () => {
    switch (true) {
      case (navigator.userAgent.indexOf("Opera") ||
        navigator.userAgent.indexOf("OPR")) !== -1:
        return "Opera";
      case navigator.userAgent.indexOf("Edg") !== -1:
        return "Edge";
      case navigator.userAgent.indexOf("Chrome") !== -1:
        return "Chrome";
      case navigator.userAgent.indexOf("Safari") !== -1:
        return "Safari";
      case navigator.userAgent.indexOf("Firefox") !== -1:
        return "Firefox";
      case navigator.userAgent.indexOf("MSIE") !== -1:
        return "IE";
      default:
        return "unknown";
    }
  };

  return (
    <Container>
      <Row>
        <Col md={{ span: 12, offset: 3 }}>
          <Typography variant="baseBold">
            {`${t("errorData.header")}`}
          </Typography>
        </Col>

        <Col md={{ span: 6, offset: 3 }}>
          <TextArea
            label={t("errorData.errorDetail")}
            ariaLabel={t("errorData.errorDetail")}
            value={errorData.errorDetail || ""}
            name={nameof<typeof errorData>("errorDetail")}
            onChange={(e) => handleChange(e, setErrorData)}
            maxLength={2000}
            rows={8}
            required
            errorText={errors?.errorDetail}
          />
        </Col>

        <Col md={{ span: 6, offset: 3 }}>
          <TextArea
            label={t("errorData.dataDetail")}
            ariaLabel={t("errorData.dataDetail")}
            value={errorData.dataDetail || ""}
            name={nameof<typeof errorData>("dataDetail")}
            onChange={(e) => handleChange(e, setErrorData)}
            placeholder="Upišite podatke sa kojima ste radili u trenutku greške ukoliko su relevantni za prijavu"
            maxLength={2000}
            rows={8}
          />
        </Col>

        <Col md={{ span: 6, offset: 3 }}>
          <TextField
            label={t("errorData.phone")}
            value={errorData.phone || ""}
            name={nameof<typeof errorData>("phone")}
            onChange={(e) => handleChange(e, setErrorData)}
          />
        </Col>

        <Col md={{ span: 6, offset: 3 }}>
          <TextField
            label={t("errorData.email")}
            value={errorData.email || ""}
            name={nameof<typeof errorData>("email")}
            onChange={(e) => handleChange(e, setErrorData)}
            type="email"
            required
            errorText={errors?.email}
          />
        </Col>
        <Col md={{ span: 6, offset: 3 }}>
          <div className={styles.confirmButtonsContainer}>
            <div>
              <Button
                color="primary"
                outline
                onClick={() => handleToggleCancelModal()}
              >
                {t("common.quit")}
              </Button>
            </div>
            <div>
              <Button color="primary" onClick={handleSend}>
                {t("common.sendError")}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Modal
        type={"confirm"}
        title={t("common.cancelModalTitle")}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isCancelModalOpened}
        onClose={handleToggleCancelModal}
        onConfirm={handleConfirmCancelModal}
      >
        {t("common.cancelModalMessage")}
      </Modal>

      <Modal
        type={"confirm"}
        title={t("common.confirmSendErrorReportTitle")}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isMessageModalOpened}
        onConfirm={handleConfirmMessageModal}
        onClose={handleToggleMessageModal}
      >
        <div className={styles.modalMessageContent}>
          {t("common.confirmSendErrorReportMessage")}
        </div>
      </Modal>
      <Modal
        type={"message"}
        title={t("errorData.header")}
        confirmText={t("common.close")}
        open={isMailSuccessfullySent}
        onClose={handleCloseIsMailSuccessfullySentModal}
        onConfirm={handleCloseIsMailSuccessfullySentModal}
      >
        {t("errorData.emailSuccessfullySent")}
      </Modal>
    </Container>
  );
};
